import type { ChatTransport, ToolSet, UIMessage, UIMessageChunk } from 'ai';
import { convertToModelMessages, generateId, generateText, stepCountIs, tool } from 'ai';
import { z } from 'zod/v4';

import type {
    SendpulseAiCredits,
    SendpulseAiProviderService,
} from '@web/store/page-structure/ai-connector/provider/sendpulse-ai-provider.service';
import { SENDPULSE_AI_PROXY_METADATA_KEY } from '@web/store/page-structure/ai-connector/provider/sendpulse-ai-provider.service';
import type { McpToolsService } from '@web/store/page-structure/ai-connector/tool-definitions/mcp-tools.service';
import type { AiSessionService } from '@web/store/page-structure/ai-connector/ai-session.service';
import { stripFunctionCallsFromText, stripFunctionResultsFromText } from '@sites/ai-connector';
import {
    activeToolNames,
    REQUEST_TOOLS_NAME,
    resolveOpenAreas,
    TOOL_AREAS,
    TOOL_AREA_NAMES,
    type ToolArea,
} from '../const/ai-chat-drawer.tool-areas';
import { RESTORED_HISTORY_NOTE } from '../const/ai-chat-drawer.prompt-core';
import { markRestoredHistory } from './ai-chat-history.util';

/**
 * Was 8, sized for the era where every widget type had its own dedicated create/update tool — one
 * step per widget created. Since 2026-09-07 every widget type is withdrawn from `TOOL_FACTORIES`
 * (`registry.ts`'s `SCHEMA_ONLY_FACTORIES` — see [[mcp-generic-widget-schema-lookup]]), so a reply
 * touching N distinct widget types for the first time costs N extra `get_widget_schema` lookup steps
 * on top of the actual create/update steps, plus the occasional retry when the model gets a field
 * name wrong. Observed live the same day: a 3-block page (intro + pizza cards + subscription form —
 * text, image, button, form, so 4 lookups alone) hit 8 and stopped mid-task, having fetched the
 * form's schema but never called `create_widget` with it. Doubled as a first, unmeasured attempt at
 * a new ceiling — not derived from a real worst-case count, so revisit if replies are still cutting
 * off, or if the added latency from more possible round trips turns out to matter more than finishing
 * the task does.
 */
const MAX_TOOL_CALL_STEPS = 16;

export interface SendpulseChatMessageMetadata {
    credits?: SendpulseAiCredits;
    customKeyFailed?: boolean;
    /** Loaded from the saved conversation rather than said in this session — see `markRestoredHistory`. */
    restored?: boolean;
}

export type SendpulseUIMessage = UIMessage<SendpulseChatMessageMetadata>;

export interface SendpulseChatTransportOptions {
    sendpulseAiProviderService: SendpulseAiProviderService;
    mcpToolsService: McpToolsService;
    /** Blocks the canvas for the duration of a reply — see `AiSessionService`. */
    aiSession: AiSessionService;
    /**
     * Built per step from the areas the request opened, not fixed: the paragraphs about the theme,
     * the catalog and widget `data` describe tools that are only sometimes on the table, and they
     * are 4 267 of the prompt's 11 625 bytes — paid on every step.
     */
    buildSystemPrompt: (areas: readonly ToolArea[] | null) => string;
    product: string;
    /** Read fresh on every send — lets the model selector change without rebuilding the transport. */
    getSelectedModel: () => string;
    /** Set true to skip the network call entirely and return this canned text instead. */
    getMockResponseText?: () => string | null;
}

/**
 * Custom `ChatTransport` running in-process (no HTTP round trip to our own backend — the actual
 * network call happens inside `generateText`, via `SendpulseAiProviderService`'s
 * `@ai-sdk/openai-compatible` model wrapped in `textToolProtocolMiddleware`).
 * `DefaultChatTransport` assumes a same-origin backend speaking AI SDK's UI-message-stream
 * protocol; `DirectChatTransport` assumes a pre-built `Agent` — neither fits this integration.
 *
 * Tool calls are first-class: every tool call/result from `generateText`'s multi-step loop —
 * whether the gateway returned it as structured `tool_calls` or `textToolProtocolMiddleware`
 * converted it from pseudo-XML text — is emitted as real `tool-*` UIMessageChunks, so assistant
 * messages carry proper tool parts. On the next turn `convertToModelMessages` turns those parts
 * back into native tool-call/tool-result model messages: the model sees a history of itself
 * calling tools through the real channel, reinforcing native tool-calling.
 *
 * Chunks are streamed per step (via `onStepFinish`), not buffered until the loop ends: the
 * multi-step tool loop makes one HTTP round trip per step, and waiting for all of them left the
 * chat showing a bare loading bar while blocks were already appearing on the page. The stream is
 * returned immediately and each completed step's text/tool chunks are enqueued as they happen.
 */
export class SendpulseChatTransport implements ChatTransport<SendpulseUIMessage> {
    public constructor(private readonly options: SendpulseChatTransportOptions) {}

    public async sendMessages({
        messages,
        abortSignal,
    }: Parameters<ChatTransport<SendpulseUIMessage>['sendMessages']>[0]): Promise<ReadableStream<UIMessageChunk>> {
        return new ReadableStream<UIMessageChunk>({
            start: (controller) => {
                void this.streamReply(messages, abortSignal, controller);
            },
        });
    }

    public async reconnectToStream(): Promise<ReadableStream<UIMessageChunk> | null> {
        // No persistent server-side stream to reconnect to — the network call is a single
        // request/response made from inside sendMessages, same rationale as DirectChatTransport.
        return null;
    }

    private async streamReply(
        messages: readonly SendpulseUIMessage[],
        abortSignal: AbortSignal | undefined,
        controller: ReadableStreamDefaultController<UIMessageChunk>
    ): Promise<void> {
        // Begun BEFORE anything can fail: every path below ends in the `finally` that clears it, so
        // an early throw cannot leave the canvas blocked.
        this.options.aiSession.begin();

        try {
            const mockText = this.options.getMockResponseText?.();

            controller.enqueue({ type: 'start' });

            if (mockText != null) {
                controller.enqueue({ type: 'start-step' });
                this.textChunks(mockText).forEach((chunk) => controller.enqueue(chunk));
                controller.enqueue({ type: 'finish-step' });
                controller.enqueue({ type: 'finish' });
                return;
            }

            // MCP tool schemas are only known once `listTools()` resolves — an actual network
            // round trip to `apps/mcp-server`, so this await is on the critical path of every
            // send. §5.1 of `ai-mcp-plan.md` tracks caching it.
            const mcpTools = await this.getMcpTools();

            /**
             * Areas the model asked for mid-answer. Mutated by `request_tools`' `execute` and read by
             * `prepareStep` on the NEXT step, which is what makes the escape hatch work: the tool set
             * only ever grows within one answer, so nothing the model has already called can vanish
             * under it.
             */
            const requestedAreas = new Set<ToolArea>();

            const tools: ToolSet = {
                ...mcpTools,
                [REQUEST_TOOLS_NAME]: tool({
                    description:
                        'Ask for the tools of one area when you do not see a tool for what the user ' +
                        'wants. You are given a subset chosen from their words, and it can be wrong. ' +
                        'Call this FIRST — before telling the user something is not possible — and the ' +
                        "area's tools become available on your next step. Areas: " +
                        `${TOOL_AREA_NAMES.join(', ')}.`,
                    inputSchema: z.object({
                        area: z.enum(TOOL_AREA_NAMES).describe('Which area of tools you need.'),
                    }),
                    execute: async ({ area }: { area: ToolArea }) => {
                        requestedAreas.add(area);

                        return { area, tools: [...TOOL_AREAS[area]] };
                    },
                }),
            };

            const available = Object.keys(tools);
            // The LAST user message only, deliberately. A follow-up like "сделай его синим" names no
            // area at all, which the selector answers with "cannot tell" — i.e. every tool — so the
            // case that would need conversation history is already the safe one.
            const asked = this.lastUserMessageText(messages);

            const result = await generateText({
                model: this.options.sendpulseAiProviderService.languageModel(
                    this.options.getSelectedModel(),
                    this.options.product
                ),
                system: this.options.buildSystemPrompt(resolveOpenAreas(asked)),
                // Prior tool parts round-trip as REAL tool-call/tool-result model messages here,
                // instead of the flattened text-only history the old manual conversion produced —
                // dropping them was hiding every past tool call from the model.
                messages: await convertToModelMessages(markRestoredHistory(messages, RESTORED_HISTORY_NOTE), {
                    tools,
                    ignoreIncompleteToolCalls: true,
                }),
                tools,
                /**
                 * The tool DEFINITIONS are 138 KB — about 35k tokens — and the proxy caches nothing,
                 * so they are re-sent on every one of up to eight steps. A request uses two to five of
                 * them. `activeTools` filters what reaches the provider (the full map stays, so
                 * `convertToModelMessages` above can still resolve a past call to a tool that is not
                 * active this turn — filtering `tools` itself would break history).
                 *
                 * Recomputed per step so `request_tools` can widen it; see
                 * `ai-chat-drawer.tool-areas.ts` for why an unrecognised request means EVERYTHING
                 * rather than nothing.
                 */
                prepareStep: () => {
                    // ONE resolution for both, on the same step. That is what keeps a rule from
                    // going missing while the tool it describes is present — including after
                    // `request_tools` has widened the set mid-answer.
                    const areas = resolveOpenAreas(asked, [...requestedAreas]);

                    return {
                        activeTools: activeToolNames(available, asked, [...requestedAreas]),
                        system: this.options.buildSystemPrompt(areas),
                    };
                },
                // No `experimental_repairToolCall` here, and none is possible as things stand:
                // `McpToolsService` builds `dynamicTool()`s from the JSON Schema `tools/list`
                // returned, so there is no operation Zod schema and no `keyAliases` for
                // `validateForgiving` to repair a malformed call against. A rejected call comes
                // back to the model as a failed tool result and it retries. If repair is wanted on
                // this path, the forgiving pass belongs server-side, in the tool handler — which is
                // where `@sites/ai-connector`'s validator already runs.
                stopWhen: stepCountIs(MAX_TOOL_CALL_STEPS),
                abortSignal,
                onStepFinish: async (step) => {
                    controller.enqueue({ type: 'start-step' });

                    for (const part of step.content) {
                        switch (part.type) {
                            case 'text':
                                this.textChunks(part.text).forEach((chunk) => controller.enqueue(chunk));
                                break;
                            case 'tool-call':
                                controller.enqueue({
                                    type: 'tool-input-available',
                                    toolCallId: part.toolCallId,
                                    toolName: part.toolName,
                                    input: part.input,
                                });
                                break;
                            case 'tool-result':
                                controller.enqueue({
                                    type: 'tool-output-available',
                                    toolCallId: part.toolCallId,
                                    output: part.output,
                                });
                                break;
                            case 'tool-error':
                                controller.enqueue({
                                    type: 'tool-output-error',
                                    toolCallId: part.toolCallId,
                                    errorText: part.error instanceof Error ? part.error.message : String(part.error),
                                });
                                break;
                        }
                    }

                    controller.enqueue({ type: 'finish-step' });

                    // §4.3: the page is re-read here, once per step, rather than mirrored per
                    // mutation. `McpToolsService` used to apply each change to the NgRx stores as it
                    // came back — a second implementation of a write the server had already made,
                    // and the source of every drift bug that path produced. What that mirror was
                    // actually FOR is this: the canvas keeping up while a multi-step reply runs.
                    // A re-read buys the same thing from the copy that is authoritative anyway.
                    //
                    // Awaited, so the next step starts against stores that match the server. The
                    // SDK awaits this callback (`Callback<EVENT> = (event) => PromiseLike<void> |
                    // void`), and the cost is one content GET per step — at most
                    // MAX_TOOL_CALL_STEPS of them, typically two or three, while the canvas is
                    // blocked and the user is watching the chat.
                    //
                    // No-ops unless a tool in this step reported changing the OPEN page, so a
                    // conversational step costs nothing. Never rejects, by its own contract.
                    await this.options.mcpToolsService.resyncOpenPageIfChanged();
                },
            });

            console.log('AI chat: raw response text received', result.text);

            const metadata =
                (result.finalStep.providerMetadata?.[
                    SENDPULSE_AI_PROXY_METADATA_KEY
                ] as SendpulseChatMessageMetadata) ?? {};

            controller.enqueue({ type: 'finish', messageMetadata: metadata });
        } catch (error: unknown) {
            const errorText = error instanceof Error ? error.message : 'Something went wrong, please try again.';

            try {
                controller.enqueue({ type: 'error', errorText });
            } catch {
                // Stream already canceled (e.g. chat.stop() aborted the request) — no one to notify.
            }
        } finally {
            // The final re-read. Per-step re-reads above cover a reply that ran to completion;
            // this one exists for the replies that did not. In `finally` rather than after a
            // successful `generateText` because the cases that end badly are the ones that need it
            // MOST — an aborted or failed loop can leave a mutation written on the server whose
            // step never finished, so `onStepFinish` never ran for it. No-ops when nothing changed,
            // and no-ops again when the last step already re-read: the flag is cleared by whichever
            // call gets there first.
            // Guarded independently of `resyncOpenPageIfChanged`'s own promise-never-rejects
            // contract: anything thrown between here and `controller.close()` strands the reply
            // stream open, and a chat that never finishes is a worse failure than a stale canvas.
            try {
                // Awaits the page re-read, so the canvas stays blocked until the stores hold the
                // page the server actually has — not just until the model stopped talking.
                await this.options.mcpToolsService.resyncOpenPageIfChanged();
            } catch {
                // Already reported by the service; nothing here can act on it.
            } finally {
                // One undo step for everything this reply changed — after the final resync, so
                // "after" reflects server truth, and exactly once per reply regardless of how many
                // steps or `onStepFinish` resyncs it took to get here (see `flushAiHistoryBatch`'s
                // own doc comment for why this can't live inside `onStepFinish` instead).
                this.options.mcpToolsService.flushAiHistoryBatch();
                this.options.aiSession.end();
            }

            try {
                controller.close();
            } catch {
                // Already closed or canceled.
            }
        }
    }

    /**
     * The text of the most recent user turn — what the tool subset is chosen from.
     *
     * Only the text parts: a message can also carry files and tool parts, and neither says anything
     * about which tools the request needs. Empty when there is no user message, which the selector
     * reads as "cannot tell" and answers with every tool.
     */
    private lastUserMessageText(messages: readonly SendpulseUIMessage[]): string {
        const last = [...messages].reverse().find((message) => message.role === 'user');

        return (last?.parts ?? [])
            .filter((part): part is { type: 'text'; text: string } => part.type === 'text')
            .map((part) => part.text)
            .join(' ');
    }

    private async getMcpTools(): Promise<ToolSet> {
        try {
            return await this.options.mcpToolsService.getTools();
        } catch (error) {
            const cause = error instanceof Error ? error.message : String(error);
            throw new Error(
                `Could not reach the MCP server (${cause}). Start it with ` +
                    '"nx run mcp-server:serve-http" and try again.'
            );
        }
    }

    private textChunks(text: string): UIMessageChunk[] {
        // `textToolProtocolMiddleware` already rewrites leaked `<function_calls>` XML into real
        // tool calls before the SDK core sees it — this strip is belt-and-braces for the one
        // shape the middleware can't catch (a hallucinated block with no `<function_calls>`
        // before it), so raw XML never reaches the displayed/stored message text.
        const cleanText = stripFunctionResultsFromText(stripFunctionCallsFromText(text));

        if (!cleanText) {
            return [];
        }

        const id = generateId();

        return [
            { type: 'text-start', id },
            { type: 'text-delta', id, delta: cleanText },
            { type: 'text-end', id },
        ];
    }
}
